import type { Video } from "@unbox/api";
import { LineChartUp02, Tag01, TrendUp01 } from "@untitledui/icons";
import { useMemo } from "react";
import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useVideoPerformance } from "@/api/video";

const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  compactDisplay: "short",
  notation: "compact",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

// Performance data point type (with nullable gmv to track null state)
interface PerformanceDataPoint {
  date: string;
  dateLabel: string;
  gmv: number | null;
}

// Performance Chart Component
export const PerformanceChart = ({
  videoId,
  currencyFormatter,
}: {
  videoId: string;
  currencyFormatter: Intl.NumberFormat;
}) => {
  const { data: performanceData, isLoading, error } = useVideoPerformance(videoId);

  const chartData: PerformanceDataPoint[] = useMemo(() => {
    if (!performanceData) return [];

    return performanceData.map((day) => ({
      date: day.date,
      dateLabel: new Date(day.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      gmv: day.gmv,
    }));
  }, [performanceData]);

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    const gmvValue = data.gmv;

    return (
      <div
        style={{
          backgroundColor: "white",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          padding: "12px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        }}
      >
        <p className="text-sm font-medium text-gray-900 mb-2">{data.dateLabel}</p>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-utility-brand-600" />
          <span className="text-sm text-gray-600">GMV:</span>
          <span className="text-sm font-medium text-gray-900">
            {gmvValue === null ? "No Data" : currencyFormatter.format(gmvValue)}
          </span>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading performance data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500">Failed to load performance data</p>
      </div>
    );
  }

  // Dynamic X-axis configuration based on data density
  const shouldRotateLabels = chartData.length > 10;
  const xAxisConfig = { angle: 0, textAnchor: "middle" as const, height: 30 };
  const chartBottomMargin = shouldRotateLabels ? 60 : 20;

  return (
    <div className="">
      <div className="inline-flex justify-start items-center gap-1.5">
        <LineChartUp02 className="w-5 h-5 text-tertiary" strokeWidth={2} />
        <div className="justify-start text-tertiary text-sm font-medium leading-tight">
          GMV breakdown over the last 30 days
        </div>
      </div>
      <div style={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: chartBottomMargin }}>
            <CartesianGrid horizontal={true} vertical={false} stroke="#f0f0f0" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={false}
              axisLine={false}
              angle={xAxisConfig.angle}
              textAnchor={xAxisConfig.textAnchor}
              height={xAxisConfig.height}
              interval="preserveStartEnd"
              minTickGap={30}
              tickMargin={20}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={false}
              axisLine={false}
              width={70}
              tickFormatter={(value) => currencyFormatter.format(value)}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="gmv"
              stroke="#7F56D9"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Metrics Grid Component
export const MetricsGrid = ({ data, currencyFormatter }: { data: Video; currencyFormatter: Intl.NumberFormat }) => {
  return (
    <div>
      <div className="pb-4 inline-flex justify-start items-center gap-1.5">
        <TrendUp01 className="w-5 h-5 text-tertiary" strokeWidth={2} />
        <div className="justify-start text-tertiary text-sm font-medium leading-tight">
          Performance over the last 30 days
        </div>
      </div>
      <div className="flex flex-col rounded-md outline-1 outline-offset-[-1px] outline-border-primary overflow-hidden">
        <div className="self-stretch inline-flex justify-between items-center">
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {percentFormatter.format(data.ctr)}
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">CTR</div>
            </div>
          </div>
          <div className="w-0 self-stretch border-l border-border-primary" />
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {percentFormatter.format(data.ctor)}
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">CTOR</div>
            </div>
          </div>
          <div className="w-0 self-stretch border-l border-border-primary" />
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {numFormatter.format(data.orders)}
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">Orders</div>
            </div>
          </div>
        </div>
        <div className="w-full h-0 border-t border-border-primary" />
        <div className="self-stretch inline-flex justify-between items-center">
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {currencyFormatter.format(data.gmv)}
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">Revenue</div>
            </div>
          </div>
          <div className="w-0 self-stretch border-l border-border-primary" />
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {currencyFormatter.format(data.gpm)}
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">GPM</div>
            </div>
          </div>
          <div className="w-0 self-stretch border-l border-border-primary" />
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {currencyFormatter.format(data.aov)}
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">AOV</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
